import { useEffect, useRef, useState } from 'react';
import { Flex, Spinner } from '@chakra-ui/react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { verifyWorkspace } from 'services/workspace';

/**
 * Decides which auth screen a visitor starts on.
 *
 * Someone returning to a workspace they already logged into should not have to
 * pick it again — but we cannot simply jump to sign-in. `verifyWorkspace` is the
 * only writer of `tenantId`, and logout clears it, so the remembered workspace is
 * re-checked here and only a passing check earns the shortcut.
 *
 * The picker deliberately does NOT auto-forward; this component is the single
 * place that decision lives. That is what makes "← Switch workspace" safe — it
 * navigates straight to /auth/workspace, which has nothing to bounce it back.
 *
 * Only ever mounted for signed-out visitors: `index.js` swaps the whole layout
 * out once there is a token and a role.
 */
const ResolveWorkspace = () => {
	const [target, setTarget] = useState(null);

	// One check per mount. Cheap insurance against a remount (and against React
	// 18's double-invoked effects, should the upgrade ever land) firing two
	// requests whose results race to set `target`.
	const startedRef = useRef(false);

	useEffect(() => {
		if (startedRef.current) return;
		startedRef.current = true;

		const last = localStorage.getItem('lastWorkspace');
		if (!last) {
			setTarget('/auth/workspace');
			return;
		}

		verifyWorkspace(last)
			.then((result) => {
				if (!result.ok) {
					// Never fall through to sign-in on a failed check. Without a fresh
					// tenantId the login fails with a far more confusing error than
					// simply being asked to pick a workspace.
					toast.info('Please choose your workspace to continue.');
					setTarget('/auth/workspace');
					return;
				}

				// Exactly what the picker sets on a successful selection, so the
				// sign-in guard needs no special case for arriving this way.
				sessionStorage.setItem('cameFromWorkspace', 'true');
				localStorage.setItem('currentWorkspace', last);
				setTarget('/auth/sign-in');
			})
			// `target` staying null means the spinner never resolves and there is no
			// way out of this screen. Storage writes can throw (private browsing,
			// quota), so the fallback has to cover the whole chain, not just the
			// request — every path out of here must reach a route.
			.catch(() => setTarget('/auth/workspace'));
	}, []);

	// No colour prop: the two auth palettes are module-local `const C` objects in
	// layouts/auth/index.js and the picker, and copying the gold hex a third time
	// is exactly what the theme rules forbid. Inheriting is the honest option.
	if (!target) {
		return (
			<Flex justify='center' align='center' minH='200px'>
				<Spinner size='lg' thickness='3px' speed='0.8s' />
			</Flex>
		);
	}

	return <Navigate to={target} replace />;
};

export default ResolveWorkspace;
